import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useRoute, useLocation } from "wouter";
import {
  useGetAdminAssessment,
  getGetAdminAssessmentQueryKey,
} from "@workspace/api-client-react";
import type { AdminDiscResult, AdminTranscriptMessage } from "@workspace/api-client-react";
import { useAdminAuth } from "@/contexts/admin-auth";
import AdminLayout from "@/components/admin-layout";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ExternalLink, Download, Cpu, Star, RefreshCw, Trash2, RotateCcw, Send } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { STATUS_LABEL, STATUS_CLASS } from "@/constants/session-status";
import { useToast } from "@/hooks/use-toast";
import { DeleteAssessmentDialog } from "@/components/delete-assessment-dialog";

const DISC_COLORS: Record<string, string> = {
  D: "bg-red-500",
  I: "bg-amber-400",
  S: "bg-emerald-500",
  C: "bg-blue-500",
};
const DISC_LABEL: Record<string, string> = {
  D: "Dominância",
  I: "Influência",
  S: "Estabilidade",
  C: "Conformidade",
};

function ScoreBar({ factor, value }: { factor: string; value: number }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{factor} — {DISC_LABEL[factor]}</span>
        <span className="font-mono text-muted-foreground">{value}</span>
      </div>
      <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", DISC_COLORS[factor] ?? "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DiscResultSection({ result, participantId, assessmentId }: { result: AdminDiscResult; participantId: string; assessmentId: string }) {
  const { authHeader } = useAdminAuth();
  const downloadPdf = async () => {
    const res = await fetch(
      `${import.meta.env.BASE_URL}api/admin/assessments/${encodeURIComponent(assessmentId)}/report`,
      { headers: { Authorization: authHeader ?? "" } },
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `disc-${participantId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Profile header */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">{result.profileName}</CardTitle>
              <p className="text-muted-foreground text-sm mt-1">
                Perfil dominante: <strong>{result.dominantProfile}</strong> · Secundário: <strong>{result.secondaryProfile}</strong>
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="button-download-pdf">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(["D", "I", "S", "C"] as const).map((f) => (
            <ScoreBar key={f} factor={f} value={result[`${f.toLowerCase()}Score` as keyof AdminDiscResult] as number} />
          ))}
        </CardContent>
      </Card>

      {/* Subtraits */}
      {result.subtraits && result.subtraits.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Subtraços</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {result.subtraits.map((st) => (
                <div key={st.name} className="flex items-center justify-between border rounded-md px-3 py-2">
                  <span className="text-sm font-medium truncate">{st.name}</span>
                  <Badge variant="outline" className={cn("ml-2 shrink-0 text-xs",
                    st.level === "alto" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    st.level === "médio" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-muted text-muted-foreground")}>
                    {st.level}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {result.summary && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Resumo interpretativo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.summary.strengths?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-emerald-700 mb-1.5">Pontos fortes</p>
                <ul className="list-disc list-inside space-y-1">
                  {result.summary.strengths.map((s, i) => <li key={i} className="text-sm text-muted-foreground">{s}</li>)}
                </ul>
              </div>
            )}
            {result.summary.challenges?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-amber-700 mb-1.5">Desafios</p>
                <ul className="list-disc list-inside space-y-1">
                  {result.summary.challenges.map((s, i) => <li key={i} className="text-sm text-muted-foreground">{s}</li>)}
                </ul>
              </div>
            )}
            {result.summary.recommendations?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-blue-700 mb-1.5">Recomendações</p>
                <ul className="list-disc list-inside space-y-1">
                  {result.summary.recommendations.map((s, i) => <li key={i} className="text-sm text-muted-foreground">{s}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const MC_BLOCKS_DISPLAY: Array<{ blockNumber: number; theme: string; statements: Array<{ dim: string; text: string }> }> = [
  { blockNumber: 1, theme: "Liderança", statements: [
    { dim: "D", text: "Assumo o controle da situação imediatamente e tomo decisões sem esperar pelo consenso do grupo." },
    { dim: "I", text: "Inspiro e energizo a equipe em torno de uma visão compartilhada, liderando pelo entusiasmo e pelo exemplo." },
    { dim: "S", text: "Cuido para que todos estejam alinhados e confortáveis antes de avançar, priorizando o bem-estar do time." },
    { dim: "C", text: "Planejo cada detalhe meticulosamente, defino papéis e processos com precisão antes de iniciar qualquer ação." },
  ]},
  { blockNumber: 2, theme: "Comunicação", statements: [
    { dim: "D", text: "Vou direto ao ponto, sou breve e objetivo — entrego a mensagem principal sem rodeios ou contexto desnecessário." },
    { dim: "I", text: "Uso histórias envolventes e linguagem expressiva para criar conexão emocional com quem me ouve." },
    { dim: "S", text: "Adapto minha mensagem ao contexto de cada pessoa e garanto que todos se sintam incluídos e compreendidos." },
    { dim: "C", text: "Estruturo minha comunicação com lógica, dados e evidências, sendo completo e preciso acima de tudo." },
  ]},
  { blockNumber: 3, theme: "Tomada de decisão", statements: [
    { dim: "D", text: "Decido rapidamente com as informações que tenho, assumindo o risco conscientemente e movendo adiante." },
    { dim: "I", text: "Confio na minha intuição e no otimismo de que vai dar certo, preferindo experimentar a ficar paralisado." },
    { dim: "S", text: "Busco consenso e me alinho com pessoas de confiança antes de decidir, evitando gerar instabilidade." },
    { dim: "C", text: "Coleto mais informações antes de decidir, analisando dados e avaliando riscos com cuidado." },
  ]},
  { blockNumber: 4, theme: "Trabalho em equipe", statements: [
    { dim: "D", text: "Defino metas claras e cobro desempenho do grupo sem hesitar, priorizando eficiência e resultado." },
    { dim: "I", text: "Uno as pessoas em torno de uma visão comum, usando energia e alegria para resolver tensões no time." },
    { dim: "S", text: "Coopero ativamente, adapto meu estilo ao grupo e apoio quem está com dificuldade, evitando conflito." },
    { dim: "C", text: "Defino papéis, responsabilidades e padrões de qualidade com clareza, cobrando rigor técnico e método." },
  ]},
  { blockNumber: 5, theme: "Conflito", statements: [
    { dim: "D", text: "Confronto diretamente sem rodeios e defendo minha posição com firmeza, sem medo de gerar tensão." },
    { dim: "I", text: "Busco reconciliação, uso carisma para suavizar a tensão e crio um ambiente onde todos se sintam ouvidos." },
    { dim: "S", text: "Cedo ou recuo para preservar a paz, preferindo harmonia a ter razão na situação." },
    { dim: "C", text: "Analiso os fatos objetivamente, apresento argumentos lógicos com dados e separo emoção da discussão." },
  ]},
  { blockNumber: 6, theme: "Mudança", statements: [
    { dim: "D", text: "Enxergo a mudança como oportunidade de influência e me posiciono rapidamente na nova estrutura." },
    { dim: "I", text: "Fico empolgado com a novidade e inspiro os colegas com uma visão positiva da transformação." },
    { dim: "S", text: "Sinto desconforto com a instabilidade e busco garantias antes de me mover, preferindo mudanças graduais." },
    { dim: "C", text: "Preciso entender detalhadamente o racional, o processo e as implicações antes de aceitar qualquer mudança." },
  ]},
  { blockNumber: 7, theme: "Prazos", statements: [
    { dim: "D", text: "Foco no essencial e corto o restante sem hesitar, entregando no prazo mesmo que signifique abrir mão da perfeição." },
    { dim: "I", text: "Uso a urgência como motivação, improviso com criatividade e mantenho o ânimo alto mesmo sob pressão." },
    { dim: "S", text: "Mantenho a calma e trabalho metodicamente mesmo sob pressão, cumprindo o que foi comprometido." },
    { dim: "C", text: "Organizo uma lista detalhada de prioridades e busco maximizar a qualidade dentro do tempo disponível." },
  ]},
  { blockNumber: 8, theme: "Detalhamento", statements: [
    { dim: "D", text: "Produzo sínteses objetivas focadas em resultados, acreditando que um bom documento cabe em poucas páginas." },
    { dim: "I", text: "Crio documentos visualmente atrativos e engajantes, usando linguagem dinâmica para inspirar o leitor." },
    { dim: "S", text: "Elaboro com cuidado e paciência, garantindo que todas as partes relevantes estejam cobertas com confiabilidade." },
    { dim: "C", text: "Mergulho em profundidade no tema, verifico cada dado e garanto precisão absoluta, sem abrir mão da qualidade técnica." },
  ]},
  { blockNumber: 9, theme: "Relacionamento", statements: [
    { dim: "D", text: "Estabeleço relações com base em competência e respeito mútuo, indo direto ao assunto desde o início." },
    { dim: "I", text: "Invisto fortemente em criar conexão pessoal desde o início, usando carisma e interesse genuíno pelo outro." },
    { dim: "S", text: "Construo relações com sinceridade e paciência, demonstrando lealdade e interesse genuíno de forma gradual." },
    { dim: "C", text: "Construo relações com base em credibilidade e consistência técnica, demonstrando seriedade e respeito." },
  ]},
  { blockNumber: 10, theme: "Motivação", statements: [
    { dim: "D", text: "Me motiva a conquista e o desafio em si — a resistência aumenta minha determinação de superar." },
    { dim: "I", text: "Me motiva o reconhecimento, a visibilidade e o impacto que causo nas pessoas ao meu redor." },
    { dim: "S", text: "Me motiva a segurança e o bem-estar das pessoas próximas — família, equipe e relações de confiança." },
    { dim: "C", text: "Me motiva a busca de excelência e aprendizado, usando momentos difíceis como oportunidade de melhoria." },
  ]},
  { blockNumber: 11, theme: "Estresse", statements: [
    { dim: "D", text: "Fico mais intenso e controlador, aumentando minha força de vontade para dominar a situação." },
    { dim: "I", text: "Fico mais emocional e impulsivo, buscando suporte e validação das pessoas ao redor para não me sentir sozinho." },
    { dim: "S", text: "Calo, internalizo e me retraio, evitando conflitos e absorvendo tudo em silêncio para não incomodar." },
    { dim: "C", text: "Isolo-me em análise, fico hipercrítico e verifico tudo várias vezes antes de agir, com medo de errar." },
  ]},
  { blockNumber: 12, theme: "Planejamento", statements: [
    { dim: "D", text: "Defino metas ambiciosas e um plano focado em resultados, com pouca tolerância para rotinas sem propósito." },
    { dim: "I", text: "Prefiro flexibilidade e espontaneidade, trabalhando melhor quando tenho liberdade para seguir a inspiração do momento." },
    { dim: "S", text: "Crio uma rotina consistente e previsível, sentindo conforto na repetição de boas práticas e no progresso gradual." },
    { dim: "C", text: "Elaboro um cronograma detalhado com metodologia clara, processos e métricas de qualidade para cada etapa." },
  ]},
];

function parseMcContent(content: string): { mostLike: string; leastLike: string } | null {
  try {
    const obj = JSON.parse(content);
    if (obj && typeof obj.mostLike === "string" && typeof obj.leastLike === "string") {
      return { mostLike: obj.mostLike, leastLike: obj.leastLike };
    }
  } catch {
    // not JSON
  }
  return null;
}

function MessageContent({ content }: { content: string }) {
  const mc = parseMcContent(content);
  if (mc) {
    return (
      <p className="leading-snug">
        <span className="font-semibold">Mais parecido:</span>{" "}
        {DISC_LABEL[mc.mostLike] ?? mc.mostLike}
        <br />
        <span className="font-semibold">Menos parecido:</span>{" "}
        {DISC_LABEL[mc.leastLike] ?? mc.leastLike}
      </p>
    );
  }
  return <p className="whitespace-pre-wrap">{content}</p>;
}

function TranscriptSection({ messages }: { messages: AdminTranscriptMessage[] }) {
  if (messages.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-12 text-center text-muted-foreground text-sm">
          Nenhuma mensagem ainda.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Transcrição do chat</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
          {messages.map((m) => {
            const isMcResponse = m.role === "user" && parseMcContent(m.content) !== null;
            const block = isMcResponse ? MC_BLOCKS_DISPLAY.find((b) => b.blockNumber === m.blockNumber) : null;
            return (
              <React.Fragment key={m.id}>
                {block && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm bg-muted text-foreground">
                      <p className="font-semibold mb-1">{block.theme}</p>
                      <ol className="space-y-1 list-none">
                        {block.statements.map((s) => (
                          <li key={s.dim} className="flex gap-1.5">
                            <span className="font-bold shrink-0 w-4">{s.dim}.</span>
                            <span>{s.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}
                <div className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm",
                  )}>
                    <MessageContent content={m.content} />
                    <p className={cn("text-xs mt-1 opacity-60", m.role === "user" ? "text-right" : "text-left")}>
                      Bloco {m.blockNumber} · {format(new Date(m.createdAt), "HH:mm")}
                    </p>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function AssessmentDetailContent({ assessmentId }: { assessmentId: string }) {
  const { logout, authHeader } = useAdminAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [regenerateSummary, setRegenerateSummary] = useState(false);
  const [refineSubtraitsAI, setRefineSubtraitsAI] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [notifyPartnersLoading, setNotifyPartnersLoading] = useState(false);
  const { data: assessment, isLoading, error } = useGetAdminAssessment(assessmentId, {
    query: { queryKey: getGetAdminAssessmentQueryKey(assessmentId) },
  });

  const handleNotifyPartners = async () => {
    setNotifyPartnersLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}api/admin/assessments/${encodeURIComponent(assessmentId)}/notify-partners-created`,
        { method: "POST", headers: { Authorization: authHeader ?? "" } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
      }
      toast({ title: "Partners notificados!", description: "O evento assessment.created foi enviado com sucesso." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ title: "Erro ao notificar partners", description: msg, variant: "destructive" });
    } finally {
      setNotifyPartnersLoading(false);
    }
  };

  const handleReopen = async () => {
    setReopenLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}api/admin/assessments/${encodeURIComponent(assessmentId)}/reopen`,
        { method: "POST", headers: { Authorization: authHeader ?? "" } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
      }
      toast({ title: "Avaliação reativada!", description: "O status voltou para pendente com prazo de 7 dias." });
      await queryClient.invalidateQueries({ queryKey: getGetAdminAssessmentQueryKey(assessmentId) });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ title: "Erro ao reativar", description: msg, variant: "destructive" });
    } finally {
      setReopenLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalcLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}api/admin/assessments/${encodeURIComponent(assessmentId)}/recalculate`,
        {
          method: "POST",
          headers: { Authorization: authHeader ?? "", "Content-Type": "application/json" },
          body: JSON.stringify({ regenerateSummary, refineSubtraits: refineSubtraitsAI }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
      }
      const parts: string[] = ["Os scores DISC foram atualizados."];
      if (refineSubtraitsAI) parts.push("Os subtraços foram refinados pela IA.");
      if (regenerateSummary) parts.push("A interpretação da IA foi regenerada.");
      parts.push("Os webhooks foram disparados.");
      const description = parts.join(" ");
      toast({ title: "Resultados recalculados!", description });
      await queryClient.invalidateQueries({ queryKey: getGetAdminAssessmentQueryKey(assessmentId) });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ title: "Erro ao recalcular", description: msg, variant: "destructive" });
    } finally {
      setRecalcLoading(false);
      setRecalcDialogOpen(false);
      setRegenerateSummary(false);
      setRefineSubtraitsAI(false);
    }
  };

  useEffect(() => {
    if (error && (error as { status?: number }).status === 401) logout();
  }, [error, logout]);

  if (isLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!assessment || error) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Avaliação não encontrada.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/assessments")}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/admin/assessments")} data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Sessões
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-mono">{assessment.participantId}</h1>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
              <span>Chave: {assessment.apiKeyName}</span>
              <Separator orientation="vertical" className="h-4" />
              <span>Criada {format(new Date(assessment.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={STATUS_CLASS[assessment.status] ?? ""}>
              {STATUS_LABEL[assessment.status] ?? assessment.status}
            </Badge>
            {assessment.status === "completed" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRecalcDialogOpen(true)}
                disabled={recalcLoading}
                data-testid="button-recalculate"
              >
                <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", recalcLoading && "animate-spin")} />
                Recalcular resultados
              </Button>
            )}
            {assessment.status === "expired" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReopen}
                disabled={reopenLoading}
                data-testid="button-reopen"
              >
                <RotateCcw className={cn("h-3.5 w-3.5 mr-1.5", reopenLoading && "animate-spin")} />
                Reativar
              </Button>
            )}
            {(assessment.status === "pending" || assessment.status === "in_progress") && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNotifyPartners}
                  disabled={notifyPartnersLoading}
                  data-testid="button-notify-partners"
                >
                  <Send className={cn("h-3.5 w-3.5 mr-1.5", notifyPartnersLoading && "animate-pulse")} />
                  {notifyPartnersLoading ? "Enviando…" : "Notificar Partners"}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={assessment.chatUrl} target="_blank" rel="noopener noreferrer" data-testid="button-open-chat">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Abrir chat
                  </a>
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="button-delete"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Excluir avaliação
            </Button>
          </div>
        </div>
      </div>

      {/* Format badge */}
      <Card className="shadow-sm">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Formato:</span>
            <span>
              {assessment.format === "multiple_choice" ? "Múltipla Escolha" : "Discursiva"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* AI usage */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="h-4 w-4 text-muted-foreground" />
            Uso de IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {assessment.totalTokens && assessment.totalTokens > 0
                ? <span className="text-foreground font-medium">{assessment.totalTokens.toLocaleString("pt-BR")} tokens</span>
                : <span>–</span>}
            </span>
            {assessment.totalTokens && assessment.totalTokens > 0 && (
              <>
                <span>·</span>
                <span>
                  {assessment.aiCostUsd && parseFloat(assessment.aiCostUsd) > 0
                    ? <span className="text-foreground font-medium">${parseFloat(assessment.aiCostUsd).toFixed(4)} USD</span>
                    : <span>–</span>}
                </span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Participant rating */}
      {assessment.status === "completed" && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="h-4 w-4 text-muted-foreground" />
              Avaliação do Participante
            </CardTitle>
          </CardHeader>
          <CardContent>
            {assessment.participantRating ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        "h-5 w-5",
                        star <= (assessment.participantRating ?? 0)
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground/30",
                      )}
                    />
                  ))}
                  <span className="ml-2 text-sm text-muted-foreground font-medium">
                    {assessment.participantRating}/5
                  </span>
                </div>
                {assessment.participantRatingComment && (
                  <p className="text-sm text-muted-foreground italic">"{assessment.participantRatingComment}"</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">–</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* DISC result (if completed) */}
      {assessment.discResult ? (
        <DiscResultSection result={assessment.discResult} participantId={assessment.participantId} assessmentId={assessmentId} />
      ) : (
        assessment.status === "completed" && (
          <Card className="shadow-sm border-amber-200 bg-amber-50">
            <CardContent className="py-4 text-sm text-amber-700">
              Avaliação concluída mas o resultado DISC ainda não foi calculado.
            </CardContent>
          </Card>
        )
      )}

      {/* Transcript */}
      <TranscriptSection messages={assessment.messages ?? []} />

      {/* Delete assessment dialog */}
      <DeleteAssessmentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        assessmentId={assessmentId}
        participantName={assessment.participantName}
        participantEmail={assessment.participantEmail}
        onDeleted={() => navigate("/admin/assessments")}
      />

      {/* Recalculate confirmation dialog */}
      <Dialog open={recalcDialogOpen} onOpenChange={(open) => { setRecalcDialogOpen(open); if (!open) { setRegenerateSummary(false); setRefineSubtraitsAI(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recalcular resultados DISC?</DialogTitle>
            <DialogDescription>
              Os scores, perfil e subtraços serão recalculados com base nas respostas já salvas. Os webhooks (parceiros e callback) serão disparados com os novos resultados. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="refine-subtraits"
                checked={refineSubtraitsAI}
                onCheckedChange={(checked) => setRefineSubtraitsAI(checked === true)}
                disabled={recalcLoading}
              />
              <Label htmlFor="refine-subtraits" className="cursor-pointer text-sm">
                Refinar subtraços com IA
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="regenerate-summary"
                checked={regenerateSummary}
                onCheckedChange={(checked) => setRegenerateSummary(checked === true)}
                disabled={recalcLoading}
              />
              <Label htmlFor="regenerate-summary" className="cursor-pointer text-sm">
                Também regenerar interpretação da IA
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRecalcDialogOpen(false); setRegenerateSummary(false); setRefineSubtraitsAI(false); }} disabled={recalcLoading}>
              Cancelar
            </Button>
            <Button onClick={handleRecalculate} disabled={recalcLoading} data-testid="button-recalculate-confirm">
              {recalcLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Recalculando…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Recalcular
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AssessmentDetailPage() {
  const [, params] = useRoute("/admin/assessments/:assessmentId");
  const assessmentId = params?.assessmentId ?? "";
  return (
    <AdminLayout>
      <AssessmentDetailContent assessmentId={assessmentId} />
    </AdminLayout>
  );
}
